import React, { useEffect } from "react";
import { z } from "zod";

import { Checkboxes } from "../../../../../components/Inputs/SelectionOptions/Checkboxes";
import { OptionSchema } from "../../../../../components/Inputs/SelectionOptions/Checkboxes/Option";
import { RadioButtons } from "../../../../../components/Inputs/SelectionOptions/RadioButtons";
import FilterPanel, { FilterPanelProps } from "../../../../../components/Panel/FilterPanel";
import Spacer from "../../../../../components/Spacer";

import useForm from "../../../../../hooks/useForm";

export const ScimLogsFilterSchema = z.object({
	type: z.array(OptionSchema).optional(),
	status: z.array(OptionSchema).optional(),
	date: OptionSchema.optional(),
});

export type ScimLogsFilter = z.infer<typeof ScimLogsFilterSchema>;

interface ScimFilterPanelProps extends FilterPanelProps {
	filters: ScimLogsFilter;
	onApplyFilters: (filters: ScimLogsFilter) => void;
}

const typeOptions = [
	{ value: "create", label: "Create" },
	{ value: "update", label: "Update" },
	{ value: "deactivate", label: "Deactivate" },
	{ value: "unknown", label: "Unknown" },
];

const statusOptions = [
	{ value: "completed", label: "Completed" },
	{ value: "failed", label: "Failed" },
];

const dateOptions = [
	{ value: "7", label: "Last 7 Days" },
	{ value: "30", label: "Last 30 Days" },
	{ value: "90", label: "Last 90 Days" },
	{ value: "", label: "Any" },
];

const ScimFilterPanel = ({ filters, isOpen, onClose, onApplyFilters }: ScimFilterPanelProps) => {
	const { state, setInitialState, resetForm, updateForm } = useForm({
		schema: ScimLogsFilterSchema,
		defaultValues: filters,
		disableWarning: true,
	});

	const handleSaveFilters = () => {
		const formattedState = { ...state };
		//remove undeeded or empty keys
		Object.keys(formattedState).forEach((key) => {
			if (formattedState[key] === null || formattedState[key] === undefined || formattedState[key].length === 0) {
				delete formattedState[key];
			}
		});
		onApplyFilters(formattedState);
		onClose && onClose();
	};

	useEffect(() => {
		if (isOpen) {
			setInitialState({ ...filters }, true);
			updateForm({ ...filters });
		} else {
			resetForm();
		}
	}, [isOpen]);

	return (
		<FilterPanel isOpen={isOpen} onClose={onClose} title="Filters" onSaveFilters={handleSaveFilters}>
			<Checkboxes
				label="Type"
				options={typeOptions}
				value={state?.type}
				onChange={(value) => updateForm({ type: value })}
			/>
			<Spacer height={2} />
			<Checkboxes
				label="Status"
				options={statusOptions}
				value={state?.status}
				onChange={(value) => updateForm({ status: value })}
			/>
			<Spacer height={2} />
			<RadioButtons
				label="Date"
				options={dateOptions}
				value={state?.date || null}
				onChange={(value) => updateForm({ date: value })}
			/>
		</FilterPanel>
	);
};

export default ScimFilterPanel;
